import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { ProgressBar, ColorDot, colorLabel } from "../components/ProgressBar";
import { ArrowLeft, Plus, Lock, BookOpen, Users } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../lib/auth";
import AppelPanel from "../components/AppelPanel";
import ChapterDatesDialog from "../components/ChapterDatesDialog";
import ChapterRow from "../components/ChapterRow";
import ChapterPlanDialog from "../components/ChapterPlanDialog";
import EvaluationDialog from "../components/EvaluationDialog";
import ChapterActionDateDialog from "../components/ChapterActionDateDialog";
import { PLAN_LOCK_DATE } from "../lib/planLock";

const todayStr = () => new Date().toISOString().slice(0, 10);

function useAssignmentDetail(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evalDialog, setEvalDialog] = useState({ open: false, chapter: null, title: "", date: "" });
  const [dateDialog, setDateDialog] = useState(null);
  const [datesEditChapter, setDatesEditChapter] = useState(null);
  const [planDialog, setPlanDialog] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [busyChapter, setBusyChapter] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/assignments/${id}`);
    setData(data);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (chapterId, fn, okMsg) => {
    if (chapterId) setBusyChapter(chapterId);
    try {
      await fn();
      await load();
      if (okMsg) toast.success(okMsg);
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    } finally {
      if (chapterId) setBusyChapter(null);
    }
  };

  const confirmDateAction = async () => {
    const { action, chapter, date } = dateDialog;
    if (!date) {
      toast.error("Choisissez une date");
      return;
    }
    const ok = await run(chapter.id, () => api.post(`/chapters/${chapter.id}/${action}`, { date }), action === "start" ? "Chapitre démarré" : "Chapitre terminé");
    if (ok) setDateDialog(null);
  };

  const submitEvaluation = async () => {
    const { chapter, title, date } = evalDialog;
    if (!chapter) return;
    if (!title.trim()) {
      toast.error("Donnez un titre à l'évaluation");
      return;
    }
    const ok = await run(chapter.id, () => api.post(`/chapters/${chapter.id}/evaluations`, { title, date: date || null }), "Évaluation ajoutée");
    if (ok) setEvalDialog({ open: false, chapter: null, title: "", date: "" });
  };

  const savePlan = async () => {
    if (!planDialog.title.trim()) {
      toast.error("Donnez un titre au chapitre");
      return;
    }
    const body = { title: planDialog.title, start_date: planDialog.start || "", end_date: planDialog.end || "" };
    const req = planDialog.chapterId
      ? () => api.put(`/chapters/${planDialog.chapterId}`, body)
      : () => api.post(`/assignments/${id}/chapters`, body);
    const ok = await run(null, req, planDialog.chapterId ? "Chapitre modifié" : "Chapitre ajouté au programme");
    if (ok) setPlanDialog(null);
  };

  const removeChapter = async (chapterId) => {
    const ok = await run(null, () => api.delete(`/chapters/${chapterId}`), "Chapitre supprimé");
    if (ok) setConfirmDeleteId(null);
  };

  const moveChapter = (chapterId, direction) => run(chapterId, () => api.post(`/chapters/${chapterId}/move`, { direction }));

  return {
    data, loading, load, busyChapter,
    evalDialog, setEvalDialog, dateDialog, setDateDialog, datesEditChapter, setDatesEditChapter,
    planDialog, setPlanDialog, confirmDeleteId, setConfirmDeleteId,
    confirmDateAction, submitEvaluation, savePlan, removeChapter, moveChapter,
  };
}

export default function AssignmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("program");
  const d = useAssignmentDetail(id);
  const { data, loading, busyChapter } = d;

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[var(--cream)]">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 text-[var(--ink-soft)]">Chargement...</div>
      </div>
    );
  }

  const canEdit = data.can_edit;
  const isDirection = user?.role === "direction";
  const isGroup = !!data.class?.is_group;
  const planLocked = canEdit && todayStr() >= PLAN_LOCK_DATE;
  const backLink = user?.role === "direction" ? "/admin" : "/teacher";
  const appelEditable = ["teacher", "assoc_admin"].includes(user?.role);

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to={backLink} className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="back-link">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        {/* Header */}
        <AssignmentHeader data={data} canEdit={canEdit} />

        <AssignmentTabs tab={tab} setTab={setTab} isGroup={isGroup} groupCount={data.group_students?.length || 0} />

        <TabContent
          tab={tab}
          isGroup={isGroup}
          data={data}
          appelEditable={appelEditable}
          programProps={{ d, data, canEdit, planLocked, isDirection, busyChapter }}
        />
      </main>

      <AssignmentDialogs d={d} />
    </div>
  );
}

function AssignmentTabs({ tab, setTab, isGroup, groupCount }) {
  return (
    <div className="mt-8 flex gap-1 border-b border-[var(--parchment-2)]" data-testid="assignment-tabs">
      <TabButton active={tab === "program"} onClick={() => setTab("program")} icon={<BookOpen size={16} />} label="Programme" testId="tab-program" />
      {isGroup ? (
        <TabButton active={tab === "students"} onClick={() => setTab("students")} icon={<Users size={16} />} label={`Élèves du groupe (${groupCount})`} testId="tab-group-students" />
      ) : (
        <TabButton active={tab === "attendance"} onClick={() => setTab("attendance")} icon={<Users size={16} />} label="Présences" testId="tab-attendance" />
      )}
    </div>
  );
}

function TabContent({ tab, isGroup, data, appelEditable, programProps }) {
  if (tab === "students" && isGroup) return <GroupStudentsList students={data.group_students || []} />;
  if (tab === "attendance" && !isGroup) {
    return (
      <div className="mt-6">
        <AppelPanel classId={data.class.id} canEdit={appelEditable} />
      </div>
    );
  }
  if (tab === "program") return <ProgramTab {...programProps} />;
  return null;
}

function PlanLockBanners({ canEdit, planLocked }) {
  if (!canEdit) return null;
  if (planLocked) {
    return (
      <div className="bg-[var(--parchment)] border border-[var(--parchment-2)] px-4 py-3 text-sm flex items-center gap-2" data-testid="plan-locked-banner">
        <Lock size={15} className="text-[var(--ink-soft)] shrink-0" />
        <span>
          <span className="font-medium">Progression prévisionnelle verrouillée depuis le 22 septembre (date butoir : lundi 21 septembre).</span> Contactez la direction pour toute modification du programme. Vous pouvez continuer à saisir votre progression réelle (dates de début, évaluations, dates de fin).
        </span>
      </div>
    );
  }
  return (
    <div className="bg-[var(--just)]/10 border border-[var(--just)]/40 px-4 py-3 text-sm flex items-center gap-2" data-testid="plan-lock-warning-banner">
      <Lock size={15} className="text-[var(--just)] shrink-0" />
      <span>
        <span className="font-medium">Progression prévisionnelle modifiable jusqu'au lundi 21 septembre 2026 inclus.</span> Après cette date, l'ajout, la modification, la suppression et le réordonnancement des chapitres seront verrouillés.
      </span>
    </div>
  );
}

function ChapterItem({ c, idx, chapters, firstActiveIdx, canEdit, planLocked, isDirection, busyChapter, d }) {
  const movable = !c.started_at;
  const canMoveUp = movable && idx > 0 && !chapters[idx - 1].started_at;
  const canMoveDown = movable && idx < chapters.length - 1 && !chapters[idx + 1].started_at;
  return (
    <ChapterRow
      chapter={c}
      idx={idx}
      isLocked={false}
      isCurrent={idx === firstActiveIdx}
      canEdit={canEdit}
      planEditable={canEdit && !planLocked}
      isDirection={isDirection}
      busy={busyChapter === c.id}
      onStart={() => d.setDateDialog({ action: "start", chapter: c, date: todayStr() })}
      onEnd={() => d.setDateDialog({ action: "end", chapter: c, date: todayStr() })}
      onAddEval={() => d.setEvalDialog({ open: true, chapter: c, title: "", date: "" })}
      onEditDates={() => d.setDatesEditChapter(c)}
      onEditPlan={() => d.setPlanDialog({ chapterId: c.id, title: c.title, start: (c.start_date || "").slice(0, 10), end: (c.end_date || "").slice(0, 10) })}
      confirmDelete={d.confirmDeleteId === c.id}
      onAskDelete={() => d.setConfirmDeleteId(c.id)}
      onConfirmDelete={() => d.removeChapter(c.id)}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onMoveUp={() => d.moveChapter(c.id, "up")}
      onMoveDown={() => d.moveChapter(c.id, "down")}
    />
  );
}

function ProgramTab({ d, data, canEdit, planLocked, isDirection, busyChapter }) {
  const firstActiveIdx = data.chapters.findIndex((c) => !c.ended_at);
  return (
    <div className="mt-6 space-y-4" data-testid="chapters-list">
      <PlanLockBanners canEdit={canEdit} planLocked={planLocked} />
      {data.chapters.map((c, idx) => (
        <ChapterItem
          key={c.id}
          c={c}
          idx={idx}
          chapters={data.chapters}
          firstActiveIdx={firstActiveIdx}
          canEdit={canEdit}
          planLocked={planLocked}
          isDirection={isDirection}
          busyChapter={busyChapter}
          d={d}
        />
      ))}
      {canEdit && !planLocked && (
        <button
          className="w-full border-2 border-dashed border-[var(--parchment-2)] hover:border-[var(--maroon)] text-[var(--ink-soft)] hover:text-[var(--maroon)] py-4 flex items-center justify-center gap-2 text-sm font-medium transition"
          onClick={() => d.setPlanDialog({ chapterId: null, title: "", start: "", end: "" })}
          data-testid="add-chapter-plan-btn"
        >
          <Plus size={16} /> Ajouter un chapitre au programme
        </button>
      )}
      {data.chapters.length === 0 && !canEdit && (
        <div className="py-8 text-center text-[var(--ink-soft)] italic bg-white border border-[var(--parchment-2)]">
          Aucun chapitre au programme.
        </div>
      )}
    </div>
  );
}

function AssignmentDialogs({ d }) {
  return (
    <>
      <EvaluationDialog value={d.evalDialog} onChange={d.setEvalDialog} onSubmit={d.submitEvaluation} />
      <ChapterActionDateDialog
        value={d.dateDialog}
        onChange={d.setDateDialog}
        onConfirm={d.confirmDateAction}
        onClose={() => d.setDateDialog(null)}
      />
      {d.datesEditChapter && (
        <ChapterDatesDialog
          chapter={d.datesEditChapter}
          onClose={() => d.setDatesEditChapter(null)}
          onSaved={async () => {
            d.setDatesEditChapter(null);
            await d.load();
          }}
        />
      )}
      <ChapterPlanDialog
        value={d.planDialog}
        onChange={d.setPlanDialog}
        onSave={d.savePlan}
        onClose={() => d.setPlanDialog(null)}
      />
    </>
  );
}

function AssignmentHeader({ data, canEdit }) {
  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-8 pb-10 border-b border-[var(--parchment-2)]">
      <div className="lg:col-span-2">
        <div className="eyebrow">{data.subject.name} · {data.class.name}</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2" data-testid="assignment-title">
          Programme annuel
        </h1>
        <div className="mt-3 text-[var(--ink-soft)]">
          Professeur · <span className="font-medium text-[var(--ink)]">{data.teacher.name}</span>
          {!canEdit && (
            <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--parchment)] text-xs">
              <Lock size={12} /> Lecture seule
            </span>
          )}
        </div>
      </div>
      <div className="bg-white border border-[var(--parchment-2)] p-5">
        <div className="eyebrow">État global</div>
        <div className="mt-3 font-serif-display text-3xl font-semibold" data-testid="overall-progress">
          {data.progress.ended} / {data.progress.total}
        </div>
        <div className="text-sm text-[var(--ink-soft)] mb-3">chapitres terminés</div>
        <ProgressBar
          percent={data.progress.percent}
          expectedPercent={data.progress.expected_percent}
          color={data.progress.color}
        />
        <div className="mt-3 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <ColorDot color={data.progress.color} />
            <span>{colorLabel(data.progress.color)}</span>
          </div>
          <span className="font-mono-spec text-[var(--muted)]">Au {new Date().toLocaleDateString("fr-FR")}</span>
        </div>
      </div>
    </div>
  );
}

function GroupStudentsList({ students }) {
  return (
    <div className="mt-6 bg-white border border-[var(--parchment-2)] p-6" data-testid="group-students-list">
      <div className="eyebrow mb-4">Élèves du groupe — toutes classes confondues</div>
      <div className="divide-y divide-[var(--parchment-2)]">
        {students.map((s, i) => (
          <div key={s.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`group-student-row-${s.id}`}>
            <div className="flex items-center gap-3">
              <span className="font-mono-spec text-xs text-[var(--muted)]">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-medium text-sm">{s.last_name.toUpperCase()} {s.first_name}</span>
            </div>
            <span className="text-xs text-[var(--ink-soft)]">{s.class_name || "—"}</span>
          </div>
        ))}
        {students.length === 0 && (
          <div className="py-4 text-sm text-[var(--ink-soft)] italic">
            Aucun élève affecté à ce niveau. La direction attribue les niveaux depuis la fiche élève.
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition ${
        active
          ? "border-[var(--maroon)] text-[var(--maroon)]"
          : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"
      }`}
    >
      {icon} {label}
    </button>
  );
}
